import { useEffect, useState } from 'react';
import { authFetchBlob, orgApiPath } from '@/core/api';

export function isDirectHttpMediaUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return /^https?:\/\//i.test(url);
}

export function useBinaryPreviewUrl(options: {
  assetId: number;
  endpoint: 'thumbnail' | 'content';
  remoteUrlHint: string | null | undefined;
}): { url: string | null; loading: boolean; failed: boolean } {
  const { assetId, endpoint, remoteUrlHint } = options;
  const direct = isDirectHttpMediaUrl(remoteUrlHint) ? remoteUrlHint! : null;

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(direct === null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (direct !== null) {
      setTimeout(() => {
        if (cancelled) return;
        setBlobUrl(null);
        setLoading(false);
        setFailed(false);
      }, 0);
      return () => {
        cancelled = true;
      };
    }
    if (endpoint === 'thumbnail' && !remoteUrlHint) {
      setTimeout(() => {
        if (cancelled) return;
        setBlobUrl(null);
        setLoading(false);
        setFailed(false);
      }, 0);
      return () => {
        cancelled = true;
      };
    }

    let objectUrl: string | null = null;

    setTimeout(() => {
      if (cancelled) return;
      setLoading(true);
      setFailed(false);
      setBlobUrl(null);
    }, 0);

    authFetchBlob(orgApiPath(`/assets/${assetId}/${endpoint}`))
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [assetId, endpoint, direct, remoteUrlHint]);

  return { url: direct ?? blobUrl, loading, failed };
}

interface AssetBinaryImageProps {
  assetId: number;
  endpoint: 'thumbnail' | 'content';
  remoteUrlHint: string | null | undefined;
  alt: string;
  className?: string;
  loadingClassName?: string;
}

export function AssetBinaryImage({
  assetId,
  endpoint,
  remoteUrlHint,
  alt,
  className,
  loadingClassName,
}: AssetBinaryImageProps) {
  const { url, loading, failed } = useBinaryPreviewUrl({ assetId, endpoint, remoteUrlHint });

  if (url) return <img src={url} alt={alt} className={className} />;

  const fallbackCls =
    loadingClassName ?? 'w-full h-full flex items-center justify-center bg-surface-container-high';

  if (loading) {
    return (
      <div className={fallbackCls}>
        <span className="material-symbols-outlined animate-spin text-primary text-2xl">
          progress_activity
        </span>
      </div>
    );
  }

  if (failed) {
    return (
      <div className={fallbackCls}>
        <span className="material-symbols-outlined text-on-surface-variant/40 text-2xl">
          broken_image
        </span>
      </div>
    );
  }

  return (
    <div className={fallbackCls}>
      <span className="material-symbols-outlined text-on-surface-variant/40 text-2xl">image</span>
    </div>
  );
}

interface AssetBinaryVideoProps {
  assetId: number;
  uriHint: string | null | undefined;
  thumbnailUrlHint: string | null | undefined;
  className?: string;
}

export function AssetBinaryVideo({
  assetId,
  uriHint,
  thumbnailUrlHint,
  className,
}: AssetBinaryVideoProps) {
  const video = useBinaryPreviewUrl({
    assetId,
    endpoint: 'content',
    remoteUrlHint: uriHint,
  });
  const poster = useBinaryPreviewUrl({
    assetId,
    endpoint: 'thumbnail',
    remoteUrlHint: thumbnailUrlHint,
  });

  if (video.url) {
    return (
      <video src={video.url} controls poster={poster.url ?? undefined} className={className} />
    );
  }

  const fallbackCls =
    className ?? 'w-full h-full flex items-center justify-center bg-surface-container-high';

  if (video.loading) {
    return (
      <div className={fallbackCls}>
        <span className="material-symbols-outlined animate-spin text-primary text-2xl">
          progress_activity
        </span>
      </div>
    );
  }

  if (video.failed) {
    return (
      <div className={fallbackCls}>
        <span className="material-symbols-outlined text-on-surface-variant/40 text-2xl">
          broken_image
        </span>
      </div>
    );
  }

  return (
    <div className={fallbackCls}>
      <span className="material-symbols-outlined text-on-surface-variant/40 text-2xl">movie</span>
    </div>
  );
}
